import { useCallback, useEffect, useRef, useState } from 'react'

/** No persistent PII cache. A changed identity hides old values synchronously. */
export function useProfileOverview({ scope, clubCode, memberId, get }) {
  const [resource, setResource] = useState({ scope, status: 'loading', data: null })
  const getRef = useRef(get)
  getRef.current = get
  const generation = useRef(0)
  const activeScope = useRef(scope)
  activeScope.current = scope
  const refresh = useCallback(async ({ afterSave = false, savedMember = null } = {}) => {
    const request = ++generation.current
    setResource(previous => {
      if (!afterSave) return { scope, status: 'loading', data: null }
      const data = previous.scope === scope ? previous.data : null
      const saved = savedMember && (!memberId || savedMember.id === memberId) ? {
        id: savedMember.id, contact_email: savedMember.contact_email ?? savedMember.email ?? null,
        phone_number: savedMember.phone_number ?? null, date_of_birth: savedMember.date_of_birth ?? null,
      } : null
      return { scope, status: data || saved ? 'resolved' : 'loading', data: data || saved ? { ...data, ...saved } : null }
    })
    try {
      // A post-save read must not coalesce with a snapshot started before PATCH.
      const revision = afterSave ? `?revision=${request}` : ''
      const response = await getRef.current(`/api/members-portal/${encodeURIComponent(clubCode)}/me/profile-overview${revision}`)
      const body = typeof response?.json === 'function' ? await response.json() : response
      if (response?.ok === false || !body?.ok || !body.data || (memberId && body.data.id !== memberId)) throw new Error('profile_overview_failed')
      if (generation.current === request && activeScope.current === scope) setResource({ scope, status: 'resolved', data: body.data })
      return true
    } catch {
      if (generation.current === request && activeScope.current === scope) setResource(previous =>
        afterSave && previous.scope === scope && previous.data
          ? { ...previous, refreshFailed: true }
          : { scope, status: 'failed', data: null })
      return false
    }
  }, [scope, clubCode, memberId])
  useEffect(() => { refresh(); return () => { generation.current += 1 } }, [refresh])
  return { ...(resource.scope === scope ? resource : { scope, status: 'loading', data: null }), refresh }
}
